import { createElement, isValidElement, type ComponentType, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import Breadcrumb, { type BreadcrumbItem } from './Breadcrumb';
import { Eyebrow, Heading, Text } from './Typography';
import type { IconVariant } from '@/components/icons';

type HeaderIcon = ComponentType<{ className?: string; variant?: IconVariant }> | ReactNode;

interface PageHeaderToolbar {
    search?: ReactNode;
    filters?: ReactNode;
    controls?: ReactNode;
}

interface PageHeaderProps {
    /** Wayfinding: breadcrumb trail and/or a leading back affordance. */
    breadcrumb?: BreadcrumbItem[];
    backTo?: string;
    onBack?: () => void;
    /** Small, quiet identity mark (Linear variant). Optional. */
    icon?: HeaderIcon;
    title?: ReactNode;
    /** Live context — a count, status chips — instead of an explanatory subtitle. */
    meta?: ReactNode;
    /** Primary + secondary actions. */
    actions?: ReactNode;
    /** The overflow ("…") menu, rendered after the actions. */
    overflow?: ReactNode;
    /** Hoisted tab bar (e.g. UnderlineTabs / SegmentedTabs). */
    tabs?: ReactNode;
    /** Integrated search / filters / controls. */
    toolbar?: PageHeaderToolbar;
    /** `bar` (A, default): dense, meta inline. `workbench` (B): icon tile, meta as chips. */
    variant?: 'bar' | 'workbench';
    sticky?: boolean;

    /** Legacy / back-compat — still rendered, de-emphasized. */
    eyebrow?: ReactNode;
    subtitle?: ReactNode;
    action?: ReactNode;

    className?: string;
    contentClassName?: string;
}

function renderIcon(icon: HeaderIcon | undefined, className: string) {
    if (!icon) return null;
    if (isValidElement(icon)) return icon;
    return createElement(icon as ComponentType<{ className?: string; variant?: IconVariant }>, {
        className,
    });
}

export default function PageHeader({
    breadcrumb,
    backTo,
    onBack,
    icon,
    title,
    meta,
    actions,
    overflow,
    tabs,
    toolbar,
    variant = 'bar',
    sticky = false,
    eyebrow,
    subtitle,
    action,
    className,
    contentClassName,
}: PageHeaderProps) {
    const isWorkbench = variant === 'workbench';
    const resolvedActions = actions ?? action;
    const hasBreadcrumb = Boolean(backTo || onBack || (breadcrumb && breadcrumb.length > 0));
    const hasToolbar = Boolean(toolbar?.search || toolbar?.filters || toolbar?.controls);
    const hasSecondRow = Boolean(tabs || hasToolbar);
    const hasTitleBlock = Boolean(eyebrow || title || meta || subtitle);

    const isMultiLine = Boolean(eyebrow || subtitle);

    const iconNode = isWorkbench
        ? renderIcon(icon, 'size-6')
        : renderIcon(icon, 'size-5 text-muted-foreground');

    const actionsNode =
        resolvedActions || overflow ? (
            <div className="flex shrink-0 flex-wrap items-center gap-2">
                {resolvedActions}
                {overflow}
            </div>
        ) : null;

    return (
        <section
            className={cn(
                'flex flex-col gap-4 border-b border-border/70 pb-4 sm:pb-5',
                sticky &&
                    // `--app-sticky-top` é publicado pelo AdminLayout com a altura do
                    // TrialBanner, que também é `sticky top-0`. As margens negativas
                    // acompanham o padding do PageShell (`p-4 sm:p-8 lg:px-10`).
                    'sticky top-[var(--app-sticky-top,0px)] z-20 -mx-4 bg-background/85 px-4 py-2 backdrop-blur sm:-mx-8 sm:px-8 lg:-mx-10 lg:px-10',
                className,
            )}
        >
            {(hasBreadcrumb || (!hasTitleBlock && actionsNode)) && (
                <div className="flex items-center justify-between gap-3">
                    {hasBreadcrumb ? (
                        <Breadcrumb items={breadcrumb ?? []} backTo={backTo} onBack={onBack} />
                    ) : (
                        <span aria-hidden="true" />
                    )}
                    {!hasTitleBlock && actionsNode}
                </div>
            )}

            {hasTitleBlock && (
                <div
                    className={cn(
                        'flex flex-col gap-4 sm:flex-row sm:justify-between',
                        isMultiLine ? 'sm:items-start' : 'sm:items-center',
                        contentClassName,
                    )}
                >
                    <div
                        className={cn(
                            'flex min-w-0 gap-3',
                            isMultiLine ? 'items-start' : 'items-center',
                        )}
                    >
                        {iconNode &&
                            (isWorkbench ? (
                                <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-brand-subtle text-brand">
                                    {iconNode}
                                </div>
                            ) : (
                                <div
                                    className={cn(
                                        'flex shrink-0 items-center',
                                        isMultiLine && 'pt-0.5',
                                    )}
                                >
                                    {iconNode}
                                </div>
                            ))}
                        <div className="min-w-0 space-y-1.5">
                            {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
                            {(title || meta) && (
                                <div
                                    className={cn(
                                        'flex min-w-0 gap-x-3 gap-y-1',
                                        isWorkbench
                                            ? 'flex-col'
                                            : 'flex-col sm:flex-row sm:flex-wrap sm:items-center',
                                    )}
                                >
                                    {title && (
                                        <Heading
                                            as="h1"
                                            variant="section"
                                            className={cn(
                                                'min-w-0 truncate',
                                                isWorkbench && 'text-2xl',
                                            )}
                                        >
                                            {title}
                                        </Heading>
                                    )}
                                    {meta && (
                                        <div
                                            className={cn(
                                                'flex flex-wrap items-center gap-2 text-sm text-muted-foreground',
                                                isWorkbench ? 'font-medium' : 'tabular-nums',
                                            )}
                                        >
                                            {meta}
                                        </div>
                                    )}
                                </div>
                            )}
                            {subtitle && <Text variant="description">{subtitle}</Text>}
                        </div>
                    </div>

                    {actionsNode}
                </div>
            )}

            {hasSecondRow && (
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    {tabs && <div className="min-w-0 overflow-x-auto sm:mr-auto">{tabs}</div>}
                    {toolbar?.search && (
                        <div className={cn('w-full', !tabs && 'sm:flex-1')}>{toolbar.search}</div>
                    )}
                    {(toolbar?.filters || toolbar?.controls) && (
                        <div className="flex items-center gap-2 sm:ml-auto">
                            {toolbar?.filters}
                            {toolbar?.controls}
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}
